'use client';
import React, { useState, useEffect } from 'react';
import { Flag, ToggleLeft, ToggleRight, Lock } from 'lucide-react';
import Icon from '@/components/ui/AppIcon';


interface FeatureFlag {
  id: number;
  module: string | null;
  featureKey: string;
  state: 'enabled' | 'disabled' | 'blocked';
  scope: 'global' | 'plan' | 'org';
  affectedOrgs?: number | null;
  updatedAt: string;
}

const stateConfig = {
  enabled: { label: 'Enabled', class: 'badge-success', icon: ToggleRight },
  disabled: { label: 'Disabled', class: 'badge-neutral', icon: ToggleLeft },
  blocked: { label: 'Blocked by Control', class: 'badge-danger', icon: Lock },
};

export default function FeatureFlagSummary() {
  const [search, setSearch] = useState('');
  const [flags, setFlags] = useState<FeatureFlag[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchFlags = async () => {
      try {
        const res = await fetch((process.env.NEXT_PUBLIC_API_URL || '') + '/api/feature-flags');
        if (res.ok) {
          const data = await res.json();
          // Filter to show module-specific flags (those with a module field)
          const moduleFlags = (data.flags || []).filter((f: FeatureFlag) => f.module);
          setFlags(moduleFlags);
        }
      } catch (err) {
        console.error('Failed to fetch feature flags:', err);
      } finally {
        setLoading(false);
      }
    };
    fetchFlags();
  }, []);

  const filtered = flags.filter(f =>
    f.featureKey.toLowerCase().includes(search.toLowerCase()) ||
    (f.module || '').toLowerCase().includes(search.toLowerCase())
  );

  const blockedCount = flags.filter(f => f.state === 'blocked').length;

  const formatDate = (dateStr: string) => {
    const d = new Date(dateStr);
    const months = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
    const day = String(d.getDate()).padStart(2, '0');
    const month = months[d.getMonth()];
    const year = d.getFullYear();
    return `${day} ${month} ${year}`;
  };

  return (
    <div className="bg-card border border-border rounded-xl overflow-hidden">
      <div className="flex items-center justify-between px-5 py-4 border-b border-border">
        <div className="flex items-center gap-2">
          <Flag size={15} className="text-warning" />
          <h3 className="text-sm font-600 text-foreground">Feature Flags & Module Policies</h3>
          {blockedCount > 0 && (
            <span className="badge-warning text-[10px] font-700 px-1.5 py-0.5 rounded-full">
              {blockedCount} blocked
            </span>
          )}
        </div>
        <input
          type="text"
          placeholder="Search flags..."
          value={search}
          onChange={e => setSearch(e.target.value)}
          suppressHydrationWarning
          className="text-[12px] bg-muted border border-border rounded-lg px-3 py-1.5 text-foreground placeholder-muted-foreground outline-none focus:border-primary w-40 transition-colors"
        />
      </div>

      <div className="overflow-x-auto">
        {loading ? (
          <div className="p-5 space-y-3">
            {[...Array(5)].map((_, i) => (
              <div key={`skel-ff-${i}`} className="h-8 bg-muted rounded animate-pulse" />
            ))}
          </div>
        ) : (
          <table className="w-full">
            <thead>
              <tr className="bg-muted/50">
                <th className="text-left text-[11px] font-600 uppercase tracking-wide text-muted-foreground px-5 py-2.5">Module</th>
                <th className="text-left text-[11px] font-600 uppercase tracking-wide text-muted-foreground px-4 py-2.5">Feature Key</th>
                <th className="text-left text-[11px] font-600 uppercase tracking-wide text-muted-foreground px-4 py-2.5">State</th>
                <th className="text-left text-[11px] font-600 uppercase tracking-wide text-muted-foreground px-4 py-2.5">Scope</th>
                <th className="text-left text-[11px] font-600 uppercase tracking-wide text-muted-foreground px-4 py-2.5">Affected</th>
                <th className="text-left text-[11px] font-600 uppercase tracking-wide text-muted-foreground px-4 py-2.5">Last Changed</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {filtered.map((flag) => {
                const cfg = stateConfig[flag.state] || stateConfig.disabled;
                const Icon = cfg.icon;
                return (
                  <tr key={flag.id} className="hover:bg-muted/40 transition-colors">
                    <td className="px-5 py-3">
                      <span className="text-[12px] font-600 text-foreground">{flag.module || '—'}</span>
                    </td>
                    <td className="px-4 py-3">
                      <span className="text-[11px] font-mono text-muted-foreground">{flag.featureKey}</span>
                    </td>
                    <td className="px-4 py-3">
                      <span className={`inline-flex items-center gap-1 text-[10px] font-600 px-2 py-0.5 rounded-full ${cfg.class}`}>
                        <Icon size={10} />
                        {cfg.label}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      <span className={`text-[10px] font-500 px-2 py-0.5 rounded-full ${
                        flag.scope === 'global' ? 'badge-primary' : flag.scope === 'plan' ? 'badge-info' : 'badge-neutral'
                      }`}>
                        {flag.scope}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      <span className="text-[12px] font-tabular text-foreground">
                        {flag.affectedOrgs ? `${flag.affectedOrgs} orgs` : '—'}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      <span className="text-[11px] text-muted-foreground">
                        {formatDate(flag.updatedAt)}
                      </span>
                    </td>
                  </tr>
                );
              })}
              {filtered.length === 0 && (
                <tr>
                  <td colSpan={6} className="px-5 py-8 text-center text-sm text-muted-foreground">
                    {loading ? 'Loading...' : 'No flags found'}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}